import { useEffect, useMemo, useState } from 'react';
import { Button, message, Rate, Select, TableColumnsType, Tag, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { QuickRangePicker } from 'components/QuickRangePicker';
import ScrollTable from 'components/ScrollTable';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { StoreInfo } from 'services/api/dashboard';
import useStoreFilter from '../useStoreFilter';
import useModal from 'hooks/useModal';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import { getSpsProblemOrders } from './service';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_RANGE_DAYS,
  FETCH_ERROR_PHRASE,
  PROBLEM_TYPE_OPTIONS,
} from './constants';
import { SpsConversation, useOpenSpsChat } from './useOpenSpsChat';
import SpsProblemOrderExportModal from './SpsProblemOrderExportModal';
import { formatIsoDateTime } from './utils';
import StatusButton from 'components/StatusButton';

type Row = Dashboard.TikTokUS.ProblemOrderRow;
type ProblemOrderMetricCode = Dashboard.TikTokUS.ProblemOrderMetricCode;

export interface SpsProblemOrderDetailProps {
  departments: Dashboard.Department[];
  /** 从排行榜指标列【明细】下钻带入 */
  initStoreInfo?: StoreInfo;
  initMetricCode?: ProblemOrderMetricCode;
}

/** 默认时间范围：近 30 天（不含当天） */
const defaultRange = (): [Dayjs, Dayjs] => [
  dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
  dayjs().subtract(1, 'day').endOf('day'),
];

/**
 * 问题订单明细
 * - 问题类型**必选、无「不限」、默认差评订单**（接口 metricCode 单值必填）
 * - 表头按所选类型**收窄**：公共列 + 该类型专属列，不适用的列不出现
 * - 页面**没有店铺列**，也不加导出才有的「外部店铺ID / 买家昵称 / 外部买家id」
 */
function SpsProblemOrderDetail({
  departments,
  initStoreInfo,
  initMetricCode,
}: SpsProblemOrderDetailProps) {
  const { t } = useTranslate();

  const exportable = useGetPermission()('导出平台数据');

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({ platform: 'TIKTOK_US', departments, initStoreInfo });

  const [metricCode, setMetricCode] = useState<ProblemOrderMetricCode>(initMetricCode ?? 'NRR');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(defaultRange());
  /** 导出弹窗：内容 = 页面那套筛选项 + 问题类型多选（不选 = 全部） */
  const [exportVisible, setExportVisible] = useState(false);

  const metricSelect = (
    <Select
      value={metricCode}
      style={{ width: 160 }}
      onChange={(value) => setMetricCode(value)}
      options={PROBLEM_TYPE_OPTIONS.map(({ value, label }) => ({ value, label: t(label) }))}
    />
  );

  const dateRangeSelect = (
    <QuickRangePicker
      quickActions={[
        {
          key: 'last7days',
          label: t('remind.近x天', { x: 7 }),
          range: [
            dayjs().subtract(7, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
        {
          key: 'last30days',
          label: t('remind.近x天', { x: 30 }),
          range: [
            dayjs().subtract(30, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
      ]}
      value={dateRange}
      onChange={(range) => {
        const [start, end] = range || [];
        if (start && end) {
          setDateRange([start, end]);
        }
      }}
    />
  );

  const rangeParams = () => ({
    platform: 'TIKTOK_US' as const,
    departmentIds: selectedDepartments.map(({ id }) => id),
    storeType:
      storeType === 'CROSS'
        ? ('跨境' as const)
        : storeType === 'LOCAL'
        ? ('本土' as const)
        : undefined,
    storeIds: storeInfo.list?.map(({ id }) => Number(id)),
    // 用户选的年月日原样传，不做时区换算
    startDate: dateRange[0].format('YYYY-MM-DD'),
    endDate: dateRange[1].format('YYYY-MM-DD'),
  });

  const { loading, data, run } = useRequest(
    (page: { pageNum: number; pageSize: number } = { pageNum: 1, pageSize }) =>
      getSpsProblemOrders({ ...rangeParams(), metricCode, ...page }),
    {
      manual: true,
      // 接口失败统一报错，不透传后端 msg
      onError: () => message.error(t(FETCH_ERROR_PHRASE)),
    },
  );

  // 排序一律吃服务端默认，前端不重排；翻页不重排
  useEffect(() => {
    run({ pageNum: 1, pageSize });
  }, [run, pageSize, metricCode, selectedDepartments, storeType, storeInfo, dateRange]);

  const openChat = useOpenSpsChat();

  /** 「查看会话」用 Modal 内嵌展示（与同模块 CSAT 明细一致） */
  const {
    visible: convVisible,
    show: showConv,
    hide: hideConv,
    meta: convMeta,
  } = useModal<SpsConversation>();

  const columns = useMemo<TableColumnsType<Row>>(() => {
    const common: TableColumnsType<Row> = [
      { key: 'orderId', dataIndex: 'orderId', title: t('订单号'), width: 160 },
      {
        key: 'product',
        title: t('商品/SKU'),
        width: 240,
        render: (_, { productName, skuId }) => (
          <div>
            <div>{productName || '-'}</div>
            {skuId && (
              <div style={{ fontSize: 12, color: 'var(--fett-text-tertiary)' }}>{skuId}</div>
            )}
          </div>
        ),
      },
      {
        key: 'orderCreateTime',
        dataIndex: 'orderCreateTime',
        title: t('下单时间'),
        width: 180,
        // ⚠️ ISO-8601，需格式化，不能用 new Date(str) 直接解析
        render: (value?: string) => formatIsoDateTime(value),
      },
    ];

    // 类型专属列
    const specific: TableColumnsType<Row> = (() => {
      switch (metricCode) {
        case 'NRR':
          return [
            {
              key: 'deliverTime',
              dataIndex: 'deliverTime',
              title: t('送达时间'),
              width: 180,
              render: (value?: string) => formatIsoDateTime(value),
            },
            {
              key: 'userReviewRating',
              dataIndex: 'userReviewRating',
              title: t('aide.买家评分'),
              width: 140,
              render: (value?: number | null) =>
                value ? <Rate disabled value={value} style={{ fontSize: 12 }} /> : '-',
            },
          ];
        case 'NBFR':
          return [
            {
              key: 'deliverTime',
              dataIndex: 'deliverTime',
              title: t('送达时间'),
              width: 180,
              render: (value?: string) => formatIsoDateTime(value),
            },
            {
              key: 'returnRefundReason',
              dataIndex: 'returnRefundReason',
              title: t('ViewRefund.退货原因'),
              render: (value?: string) => value || '-',
            },
          ];
        case 'SFCR':
          return [
            {
              key: 'cancellationReasons',
              dataIndex: 'cancellationReasons',
              title: t('取消原因'),
              // ⚠️ 英文逗号拼接，需拆分展示
              render: (value?: string) => {
                const reasons = value?.split(',').filter(Boolean) ?? [];
                if (!reasons.length) {
                  return '-';
                }
                return reasons.map((reason, index) => (
                  <Tag key={`${reason}-${index}`}>{reason.trim()}</Tag>
                ));
              },
            },
          ];
        case 'OTDR':
          return [
            {
              key: 'expectDeliverTime',
              dataIndex: 'expectDeliverTime',
              title: t('预计送达时间'),
              width: 180,
              render: (value?: string) => formatIsoDateTime(value),
            },
            {
              key: 'actualDeliverTime',
              dataIndex: 'actualDeliverTime',
              title: t('实际送达时间'),
              width: 180,
              render: (value?: string) => formatIsoDateTime(value),
            },
          ];
        default:
          return [];
      }
    })();

    return [
      ...common,
      ...specific,
      {
        key: 'operate',
        title: t('操作'),
        width: 120,
        fixed: 'right' as const,
        render: (_, record) =>
          record.orderId && record.externalStoreId && record.externalBuyerId ? (
            <StatusButton
              type="link"
              onClick={async () => {
                // 优先用 externalBuyerId 反查；为空时 hook 内自动回落到按订单号查
                const found = await openChat({
                  orderId: record.orderId,
                  externalStoreId: record.externalStoreId,
                  externalBuyerId: record.externalBuyerId,
                  buyerNick: record.buyerNick,
                  region: record.region,
                });
                if (found) {
                  showConv(found);
                }
              }}
            >
              {t('查看会话')}
            </StatusButton>
          ) : (
            '-'
          ),
      },
    ];
  }, [metricCode, openChat, showConv, t]);

  return (
    <Container>
      <header>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
        {metricSelect}
        <div className="date-range">{dateRangeSelect}</div>
      </header>
      <section>
        <header>
          <h3>{t('问题订单明细')}</h3>
          {exportable && (
            <Tooltip title={t('导出详细数据')}>
              <Button icon={<DownloadOutlined />} onClick={() => setExportVisible(true)} />
            </Tooltip>
          )}
        </header>
        <div>
          <ScrollTable
            loading={loading}
            rowKey={(row) => `${row.orderId}-${row.skuId}-${row.metricCode}`}
            columns={columns}
            dataSource={data?.list}
            pagination={{
              current: data?.pageNum || 1,
              pageSize: data?.pageSize || pageSize,
              total: data?.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (x) => t('feedback.共x项', { x }),
              onChange: (pageNum, size) => {
                setPageSize(size);
                run({ pageNum, pageSize: size });
              },
            }}
          />
        </div>
      </section>

      {convMeta && (
        <ConversationModal visible={convVisible} onCancel={() => hideConv()} record={convMeta} />
      )}

      <SpsProblemOrderExportModal
        departments={departments}
        // 初始值取页面当前筛选，保证弹窗「和页面一致」（弹窗内的调整不影响页面）
        init={{ selectedDepartments, storeType, storeInfo, metricCode, dateRange }}
        visible={exportVisible}
        onCancel={() => setExportVisible(false)}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: var(--fett-bg-panel);
    > .date-range {
      margin-left: auto;
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    > header {
      flex: 0 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 56px;
      padding: 0 16px;
      margin-bottom: 1px;
      background: var(--fett-bg-panel);
      > h3 {
        margin-bottom: 0;
      }
    }
    > div {
      flex: 1 0 0;
      padding: 16px;
      background: var(--fett-bg-panel);
    }
  }
`;

export default SpsProblemOrderDetail;
